import { useEffect, useState, useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  TextField,
  Chip,
  Alert,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import useConsentTrials from "../../hooks/useConsentTrials";
import useConsentTrialMutation from "../../hooks/useConsentTrialMutation";
import useGetDocusealTemplates from "../../hooks/useGetDocusealTemplates";
import useGetTrialConsentImpact from "../../hooks/useGetTrialConsentImpact";
import useChangeTrialConsentMutation from "../../hooks/useChangeTrialConsentMutation";
import { useEcrfContext } from "../../context/EcrfContext";
import { isTrialConfigFinalized, showApiErrorToast } from "../../utils/helpers";

import Loader from "../Loader";
import CustomButton from "../UI/CustomButton";

const VALID_SIGNING_ROLES = [
  "patient",
  "investigator",
  "sub_investigator",
  "inv_coordinator",
  "cro admin",
  "cro_coordinator",
  "reviewer",
];

const TrialEConsent = ({
  trialDetails,
  setTrialDetails,
  hasBasicInfoChanged,
  setHasBasicInfoChanged,
  handleBasicInfoUpdate,
}) => {
  const isDigitalConsent = trialDetails?.show_digital_consent;
  const isEConsent = trialDetails?.show_econsent;

  const { data: consentData, isLoading: consentLoading } = useConsentTrials({
    enabled: isEConsent,
  });
  const { data: templatesData, isLoading: templateLoading } = useGetDocusealTemplates({
    enabled: isDigitalConsent,
  });

  const consentTrialMutation = useConsentTrialMutation();
  const changeConsentMutation = useChangeTrialConsentMutation();
  const { refetch: fetchConsentImpact, isFetching: isFetchingConsentImpact } =
    useGetTrialConsentImpact({ trialId: trialDetails?.id });

  // The saved trial, as opposed to trialDetails which holds unsaved edits
  const { trial, setTrial } = useEcrfContext();
  // A finalized Live trial's configuration is locked, so its consent is changed through its own endpoint
  const isConfigFinalized = isTrialConfigFinalized(trial);

  const [selectedConsentId, setSelectedConsentId] = useState("");
  const [resetConfirm, setResetConfirm] = useState({ open: false, patientCount: 0 });
  const [openDialog, setOpenDialog] = useState(false);
  const [newConsent, setNewConsent] = useState({
    title: "",
    content: "",
    title_de: "",
    content_de: "",
    title_es: "",
    content_es: "",
    enabled_de: false,
    enabled_es: false,
  });

  const isLoading = isDigitalConsent ? templateLoading : consentLoading;

  const options = useMemo(() => {
    if (isDigitalConsent) return templatesData || [];
    if (isEConsent) return consentData || [];
    return [];
  }, [isDigitalConsent, isEConsent, templatesData, consentData]);

  const selectedTemplate = useMemo(() => {
    if (!isDigitalConsent) return null;
    const templateId = selectedConsentId || trialDetails?.consent_template_id;
    if (!templateId) return null;
    return options.find((t) => t.id === templateId) || null;
  }, [isDigitalConsent, options, selectedConsentId, trialDetails?.consent_template_id]);

  const templateRoleValidation = useMemo(() => {
    if (!selectedTemplate || !selectedTemplate.submitters) return null;
    const roles = selectedTemplate.submitters.map((sub) => {
      const name = sub.name || "";
      const isValid = VALID_SIGNING_ROLES.includes(name.trim().toLowerCase());
      return { name, valid: isValid };
    });
    const allValid = roles.every((r) => r.valid);
    return { valid: allValid, roles };
  }, [selectedTemplate]);

  const selectedValue = useMemo(() => {
    if (isDigitalConsent) {
      return trialDetails?.consent_template_id || "";
    }

    if (isEConsent) {
      return (
        (trialDetails?.consent_form?.length > 0 && trialDetails.consent_form[0]) ||
        selectedConsentId
      );
    }

    return "";
  }, [isDigitalConsent, isEConsent, trialDetails, selectedConsentId]);

  useEffect(() => {
    if (!selectedConsentId) return;

    setTrialDetails((prev) => {
      if (isDigitalConsent) {
        return {
          ...prev,
          consent_template_id: selectedConsentId,
        };
      }

      if (isEConsent) {
        return {
          ...prev,
          consent_form: [selectedConsentId],
        };
      }

      return prev;
    });

    if (templateRoleValidation && !templateRoleValidation.valid) {
      setHasBasicInfoChanged(false);
    } else {
      setHasBasicInfoChanged(true);
    }
  }, [selectedConsentId, isDigitalConsent, isEConsent, templateRoleValidation]);

  const handleLangToggle = (lang, enabled) => {
    setNewConsent((prev) => ({
      ...prev,
      [`enabled_${lang}`]: enabled,
      ...(!enabled && { [`title_${lang}`]: "", [`content_${lang}`]: "" }),
    }));
  };

  const handleAddConsent = () => {
    consentTrialMutation.mutate(
      {
        title: newConsent.title,
        content: newConsent.content,
        title_de: newConsent.enabled_de ? newConsent.title_de : "",
        content_de: newConsent.enabled_de ? newConsent.content_de : "",
        title_es: newConsent.enabled_es ? newConsent.title_es : "",
        content_es: newConsent.enabled_es ? newConsent.content_es : "",
      },
      {
        onSuccess: () => {
          setOpenDialog(false);
          setNewConsent({
            title: "",
            content: "",
            title_de: "",
            content_de: "",
            title_es: "",
            content_es: "",
            enabled_de: false,
            enabled_es: false,
          });
        },
      }
    );
  };

  const savedConsentId = isDigitalConsent
    ? trial?.consent_template_id
    : isEConsent
      ? trial?.consent_form?.[0]
      : undefined;
  const isConsentChanged =
    selectedConsentId !== "" && String(selectedConsentId) !== String(savedConsentId ?? "");

  const saveConsent = () => {
    setResetConfirm({ open: false, patientCount: 0 });
    if (!isConfigFinalized) {
      handleBasicInfoUpdate();
      return;
    }

    changeConsentMutation.mutate(
      {
        trialId: trialDetails.id,
        ...(isDigitalConsent
          ? { consentTemplateId: selectedConsentId }
          : { consentForm: selectedConsentId }),
      },
      {
        onSuccess: (result) => {
          const savedConsent = {
            consent_form: result.consent_form,
            consent_template_id: result.consent_template_id,
          };
          setTrial((prev) => ({ ...prev, ...savedConsent }));
          setTrialDetails((prev) => ({ ...prev, ...savedConsent }));
          setSelectedConsentId("");
          setHasBasicInfoChanged(false);
          toast.success(
            result.reset_patient_count > 0
              ? `Consent updated. ${result.reset_patient_count} patient(s) must sign the new consent.`
              : result.status
          );
        },
        onError: (error) => showApiErrorToast(error, "Could not update the trial consent."),
      }
    );
  };

  const handleSave = async () => {
    if (!isConsentChanged) {
      saveConsent();
      return;
    }

    const { data, isError } = await fetchConsentImpact();
    if (isError) {
      toast.error("Could not check which patients are affected by the consent change.");
      return;
    }
    if (data?.affected_patient_count > 0) {
      setResetConfirm({ open: true, patientCount: data.affected_patient_count });
    } else {
      saveConsent();
    }
  };

  const isSaveDisabled =
    (isConfigFinalized ? !isConsentChanged : !hasBasicInfoChanged) ||
    changeConsentMutation.isPending ||
    isFetchingConsentImpact ||
    (isDigitalConsent && templateRoleValidation && !templateRoleValidation.valid);

  if (isLoading) return <Loader />;

  return (
    <Box sx={{ position: "relative", width: "100%", mb: 2 }}>
      {/* Save button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 2,
        }}
      >
        {/* Selector */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
          <FormControl sx={{ width: "50%" }} size="small">
            <InputLabel id="consent-select-label">Consent Form</InputLabel>

            <Select
              labelId="consent-select-label"
              id="consent-select"
              label="Consent Form"
              value={selectedValue}
              onChange={(e) => setSelectedConsentId(e.target.value)}
            >
              {options.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {isDigitalConsent ? item.name : item.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Add consent only for eConsent */}
          {isEConsent && (
            <CustomButton
              id="add-consent-btn"
              onClick={() => setOpenDialog(true)}
              size="small"
              sx={{ width: "20%" }}
            >
              Add Consent
            </CustomButton>
          )}

          {/* Submitter role chips for digital consent */}
          {isDigitalConsent && templateRoleValidation && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {templateRoleValidation.roles.map((role) => (
                  <Chip
                    key={role.name}
                    label={role.name}
                    color={role.valid ? "default" : "error"}
                    variant={role.valid ? "outlined" : "filled"}
                    size="small"
                  />
                ))}
              </Box>
              {!templateRoleValidation.valid && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This template contains unrecognised signing roles. Please use a template with
                  valid roles: {VALID_SIGNING_ROLES.join(", ")}.
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {/* Add Consent Dialog (eConsent only) */}
        {isEConsent && (
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
            <DialogTitle>Add New Consent</DialogTitle>

            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Title"
                fullWidth
                value={newConsent.title}
                onChange={(e) => setNewConsent((prev) => ({ ...prev, title: e.target.value }))}
              />
              <TextField
                margin="dense"
                label="Content"
                fullWidth
                multiline
                rows={4}
                value={newConsent.content}
                onChange={(e) =>
                  setNewConsent((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
              />

              {[
                { key: "de", label: "German (DE)" },
                { key: "es", label: "Spanish (ES)" },
              ].map(({ key, label }) => (
                <Box key={key} sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={newConsent[`enabled_${key}`]}
                        onChange={(e) => handleLangToggle(key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{label}</Typography>}
                  />
                  {newConsent[`enabled_${key}`] && (
                    <>
                      <TextField
                        margin="dense"
                        label={`Title (${key.toUpperCase()})`}
                        fullWidth
                        value={newConsent[`title_${key}`]}
                        onChange={(e) =>
                          setNewConsent((prev) => ({
                            ...prev,
                            [`title_${key}`]: e.target.value,
                          }))
                        }
                      />
                      <TextField
                        margin="dense"
                        label={`Content (${key.toUpperCase()})`}
                        fullWidth
                        multiline
                        rows={3}
                        value={newConsent[`content_${key}`]}
                        onChange={(e) =>
                          setNewConsent((prev) => ({
                            ...prev,
                            [`content_${key}`]: e.target.value,
                          }))
                        }
                      />
                    </>
                  )}
                </Box>
              ))}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button
                onClick={handleAddConsent}
                variant="contained"
                disabled={consentTrialMutation.isLoading}
              >
                {consentTrialMutation.isLoading ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <CustomButton
          size="small"
          id="consent-form-settings"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          Save
        </CustomButton>
      </Box>

      <Dialog
        open={resetConfirm.open}
        onClose={() => setResetConfirm({ open: false, patientCount: 0 })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Trial Consent</DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 1 }}>
            {resetConfirm.patientCount} active patient(s) (Continuing or Pre-Screening) will have
            their consent reset and must sign the new consent again. Their current signature,
            signing date and Docuseal submission are cleared. Completed, withdrawn and screen-failed
            patients are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setResetConfirm({ open: false, patientCount: 0 })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            data-testid="confirm-consent-change-btn"
            onClick={saveConsent}
            disabled={changeConsentMutation.isPending}
          >
            Change Consent
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrialEConsent;
